export const getYoutubeId = (url) => {
  const patterns = [
    /youtu\.be\/([^?&#/]+)/i,
    /youtube\.com\/watch\?[^#]*v=([^?&#]+)/i,
    /youtube\.com\/embed\/([^?&#/]+)/i,
    /youtube\.com\/shorts\/([^?&#/]+)/i,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }

  return null;
};

export const getVimeoId = (url) => {
  const match = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/i);
  return match?.[1] || null;
};

export const isVideoUrl = (url) => {
  if (!url || typeof url !== "string") return false;

  const trimmed = url.trim();
  if (!trimmed) return false;

  return Boolean(
    getYoutubeId(trimmed) ||
      getVimeoId(trimmed) ||
      /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(trimmed)
  );
};

// Build the embed for the open modal. `start` (seconds) seeks the video to a
// chapter timecode and works for direct files, YouTube and Vimeo because the
// modal mounts fresh on every open, so the start time is baked into the URL.
export const getVideoEmbed = (url, start) => {
  if (!url) return null;

  const startSeconds =
    Number.isFinite(start) && start > 0 ? Math.floor(start) : 0;

  const youtubeId = getYoutubeId(url);
  if (youtubeId) {
    const params = `autoplay=1&rel=0&playsinline=1&enablejsapi=1${
      startSeconds ? `&start=${startSeconds}` : ""
    }`;
    return {
      provider: "youtube",
      type: "iframe",
      src: `https://www.youtube.com/embed/${youtubeId}?${params}`,
      title: "YouTube video",
    };
  }

  const vimeoId = getVimeoId(url);
  if (vimeoId) {
    return {
      provider: "vimeo",
      type: "iframe",
      src: `https://player.vimeo.com/video/${vimeoId}?autoplay=1${
        startSeconds ? `#t=${startSeconds}s` : ""
      }`,
      title: "Vimeo video",
    };
  }

  return {
    provider: "file",
    type: "video",
    src: startSeconds ? `${url}#t=${startSeconds}` : url,
    title: "Tutorial video",
  };
};

// Parse "90", "1:30" or "1:02:03" into seconds.
export const parseTimeToSeconds = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const str = String(value).trim();
  if (!str) return null;

  const parts = str.split(":").map((part) => Number(part));
  if (parts.some((part) => Number.isNaN(part))) return null;

  return parts.reduce((total, part) => total * 60 + part, 0);
};

export const resolveVideoUrl = (source, fallback) => {
  if (!source) return fallback || null;
  if (source.videoUrl) return source.videoUrl;
  if (source.url && isVideoUrl(source.url)) return source.url;
  return fallback || null;
};

const isVideoTriggerUrl = (url) => {
  const trimmed = (url || "").trim();
  return (
    !trimmed ||
    trimmed === "#" ||
    trimmed.startsWith("#tutorials") ||
    trimmed.startsWith("#video")
  );
};

export const resolveTipVideoUrl = (tip, sectionVideoUrl) => {
  const direct = resolveVideoUrl(tip, null);
  if (direct) return direct;

  if (!sectionVideoUrl || !isVideoTriggerUrl(tip?.url)) return null;

  return sectionVideoUrl;
};

export const shouldOpenVideoModal = (button, sectionVideoUrl) => {
  const direct = resolveVideoUrl(button, null);
  if (direct) return direct;

  if (!sectionVideoUrl || !isVideoTriggerUrl(button?.url)) return null;

  return sectionVideoUrl;
};
